import React, { createContext, useContext, useState, useEffect } from 'react';
import { auth, firestore, initFirebase } from '../firebase-config';
import {
  signInWithEmailAndPassword, createUserWithEmailAndPassword, signOut,
  onAuthStateChanged, GoogleAuthProvider, signInWithPopup,
  signInAnonymously, sendPasswordResetEmail
} from 'firebase/auth';

const FirebaseContext = createContext(null);
export const useFirebase = () => {
  const context = useContext(FirebaseContext);
  if (!context) throw new Error('useFirebase must be used within a FirebaseProvider');
  return context;
};

export const FirebaseProvider = ({ children }) => {
  const [session, setSession] = useState({ user: null, loading: true, error: null });

  useEffect(() => {
    try {
      const { error } = initFirebase();
      if (error) throw error;
      if (!auth) throw new Error('Authentication is unavailable.');
      // Firebase waits for its persisted session before this first callback.
      return onAuthStateChanged(auth,
        user => setSession({ user, loading: false, error: null }),
        error => setSession({ user: null, loading: false, error })
      );
    } catch (error) {
      setSession({ user: null, loading: false, error });
    }
  }, []);

  const value = {
    ...session,
    firebaseReady: !session.loading && !session.error,
    auth,
    firestore,
    login: (email, password) => signInWithEmailAndPassword(auth, email.trim(), password),
    register: (email, password) => createUserWithEmailAndPassword(auth, email.trim(), password),
    loginWithGoogle: () => signInWithPopup(auth, new GoogleAuthProvider()),
    loginAsGuest: () => signInAnonymously(auth),
    logout: () => signOut(auth),
    resetPassword: async email => {
      try {
        await sendPasswordResetEmail(auth, email.trim());
      } catch (error) {
        // Keep the same response whether this email has an account or not.
        if (error.code !== 'auth/user-not-found') throw error;
      }
    }
  };
  return <FirebaseContext.Provider value={value}>{children}</FirebaseContext.Provider>;
};

export default FirebaseContext;
